/**
 * Componente do mapa interativo com Leaflet, Clustering inteligente e suporte a Dark Mode.
 *
 * Renderiza os pins geocodificados com cores vibrantes por categoria.
 * Agrupa marcadores próximos via MarkerClusterGroup com cores da categoria dominante.
 * Inclui o algoritmo applySpiralOffsets para desempilhar pins com coordenadas idênticas no mesmo endereço.
 *
 * @module components/MapView
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GeoJSON,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

import { usePins, useSelectedIndicacao } from "../hooks/useIndicacoes";
import { useFilters } from "../hooks/useFilters";
import { canonicalizeBairro } from "../constants/neighborhoods";
import {
  CATEGORY_STYLES,
  THEMATIC_CATEGORIES,
  getCategoryLabel,
  getCategoryStyle,
  getGeometryMetadata,
  getRelationLabel,
} from "../constants/categories";
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  TILE_ERROR_FALLBACK_THRESHOLD,
  getTileLayerConfig,
  isErrorTileSource,
  reduceTileLayerHealth,
} from "../constants/mapConfig";
import HeatmapLayer from "./HeatmapLayer";
import MapToolbar from "./MapToolbar";
import GeometryIcon from "./GeometryIcon";
import VectorBasemapLayer from "./VectorBasemapLayer";
import NeighborhoodHighlightLayer from "./NeighborhoodHighlightLayer";
import ChoroplethLayer from "./ChoroplethLayer";

export {
  CATEGORY_STYLES,
  THEMATIC_CATEGORIES,
  getCategoryLabel,
  getCategoryStyle,
  getGeometryMetadata,
  getRelationLabel,
};

// --- Blindagens de Resiliência no Leaflet e MarkerCluster ---

// 1. Previne erro fatal "Cannot read properties of undefined (reading 'x')" em L.DistanceGrid._sqDist
if (L.DistanceGrid && !L.DistanceGrid.prototype._safeSqDistPatched) {
  L.DistanceGrid.prototype._safeSqDistPatched = true;
  const originalSqDist = L.DistanceGrid.prototype._sqDist;
  L.DistanceGrid.prototype._sqDist = function (p, p2) {
    if (!p || !p2 || typeof p.x !== "number" || typeof p2.x !== "number") {
      return Infinity;
    }
    return originalSqDist.call(this, p, p2);
  };
}

// 2. Previne re-indexação indevida no cluster quando um marcador spiderfied é atualizado pelo React
if (L.MarkerClusterGroup && !L.MarkerClusterGroup.prototype._safeChildMarkerMovedPatched) {
  L.MarkerClusterGroup.prototype._safeChildMarkerMovedPatched = true;
  const originalChildMarkerMoved = L.MarkerClusterGroup.prototype._childMarkerMoved;
  L.MarkerClusterGroup.prototype._childMarkerMoved = function (e) {
    if (e?.target?._spiderLeg || e?.target?._preSpiderfyLatlng || e?.target?.__parent?._isSpiderfied) {
      return;
    }
    return originalChildMarkerMoved?.apply(this, arguments);
  };
}

/** Distância mínima em pixels na tela entre dois clusters para permitir múltiplos spiderfies simultâneos sem colisão de pernas */
const SPIDERFY_COLLISION_DISTANCE_PX = 120;

function logBestEffortCleanupError(error) {
  if (import.meta.env.DEV) {
    console.debug("[MapView] Limpeza Leaflet ignorada:", error);
  }
}

/**
 * Verifica se todos os marcadores dentro de um cluster compartilham a mesma localização física
 * ou conjunto colocalizado (mesmo prédio, lote, praça ou complexo público com extensão de até ~25-30m).
 *
 * Conforme a referência do map-gl-js-spiderfy, o spiderfy desempilha pontos colocalizados
 * que o zoom não separaria naturalmente no mapa.
 *
 * @param {any[]} childMarkers
 * @param {L.MarkerCluster} [cluster]
 * @returns {boolean}
 */
function areMarkersAtSameLocation(childMarkers, cluster) {
  if (!childMarkers || childMarkers.length < 2) return false;
  if (childMarkers.length > 60) return false;

  // Checagem rápida O(1): se os limites do cluster forem maiores que ~30 metros, não são colocalizados
  if (cluster && typeof cluster.getBounds === "function") {
    try {
      const bounds = cluster.getBounds();
      if (bounds && bounds.isValid()) {
        const latDiff = Math.abs(bounds.getNorth() - bounds.getSouth());
        const lngDiff = Math.abs(bounds.getEast() - bounds.getWest());
        if (latDiff >= 0.0003 || lngDiff >= 0.0003) {
          return false;
        }
      }
    } catch (e) {
      logBestEffortCleanupError(e);
    }
  }

  const first = childMarkers[0].getLatLng();
  if (!first || typeof first.lat !== "number" || typeof first.lng !== "number") return false;

  // Tolerância de 0.00025 graus (~25 metros): engloba todas as indicações no mesmo complexo/endereço/praça
  return childMarkers.every((m) => {
    const ll = m.getLatLng();
    if (!ll || typeof ll.lat !== "number" || typeof ll.lng !== "number") return false;
    return (
      Math.abs(ll.lat - first.lat) < 0.00025 &&
      Math.abs(ll.lng - first.lng) < 0.00025
    );
  });
}

/**
 * Cria o ícone personalizado de cluster, definindo o visual pela categoria dominante.
 * Mantém o cluster sempre com visual neutro e limpo (sem evidência de seleção).
 *
 * @param {L.MarkerCluster} cluster
 * @returns {L.DivIcon}
 */
function createClusterCustomIcon(cluster) {
  const count = cluster.getChildCount();
  const childMarkers = cluster.getAllChildMarkers();

  // Para clusters volumosos (> 60), amostra os primeiros 60 para apuração ágil da categoria dominante
  const sampleLimit = Math.min(childMarkers.length, 60);
  const catCounts = {};

  for (let i = 0; i < sampleLimit; i++) {
    const cat = childMarkers[i]?.options?.categoryName || "Outros";
    catCounts[cat] = (catCounts[cat] || 0) + 1;
  }

  // Encontra a categoria dominante
  let dominantCategory = "Outros";
  let maxCount = -1;
  Object.entries(catCounts).forEach(([cat, c]) => {
    if (c > maxCount) {
      maxCount = c;
      dominantCategory = cat;
    }
  });

  const style = getCategoryStyle(dominantCategory);
  const size = count < 10 ? 34 : count < 50 ? 40 : 46;
  const fontSize = count < 100 ? 12 : 11;
  const uniqueCats = Object.keys(catCounts).length;

  const isSamePoint = areMarkersAtSameLocation(childMarkers, cluster);
  const tooltipText = `${count} indicações${isSamePoint ? " no mesmo endereço" : ""} (${dominantCategory} predominante${
    uniqueCats > 1 ? ` · ${uniqueCats} temas` : ""
  })`;

  return L.divIcon({
    className: "custom-cluster-icon",
    html: `
      <div class="cluster-bubble" style="
        width: ${size}px;
        height: ${size}px;
        background: ${style.solid || style.bgColor};
        color: ${style.onSolid || "#ffffff"};
        font-size: ${fontSize}px;
      " title="${tooltipText}">
        <span>${count}</span>
      </div>
      <div class="cluster-close-btn" title="Recolher agrupamento" role="button" aria-label="Recolher agrupamento">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Cache estático de instâncias de L.divIcon para eliminar alocação e destruição repetitiva de nós DOM */
const pinIconCache = new Map();

/**
 * Cria um L.divIcon estilizado dinamicamente conforme as especificações visuais de tema.
 *
 * @param {object} params
 * @param {string} params.categoryName
 * @param {boolean} params.isApproximate
 * @param {boolean} params.isSelected
 * @returns {L.DivIcon}
 */
function createCategoryPinIcon({ categoryName, isApproximate, isSelected }) {
  const style = getCategoryStyle(categoryName);
  const bgColor = style.solid || style.bgColor;
  const iconColor = style.onSolid || style.iconColor || "#ffffff";
  const borderStyle = isSelected
    ? "2.5px solid #ffffff"
    : style.borderColor
    ? `2px solid ${style.borderColor}`
    : "2px solid #ffffff";
  const size = isSelected ? 40 : 28;
  const iconSize = isSelected ? 21 : 15;

  const approxDotHtml = isApproximate
    ? `<div style="position:absolute; top:-2px; right:-2px; width:10px; height:10px; border-radius:50%; background:var(--status-warning-fg, #b45309); border:2px solid #ffffff; box-shadow:0 2px 5px rgba(0,0,0,0.3); z-index:5;" title="Endereço aproximado"></div>`
    : "";

  const svgHtml = `
    <div class="custom-pin-marker ${isSelected ? "pin-spotlight-active" : ""}" style="
      position: relative;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      background: ${bgColor};
      border: ${borderStyle};
      color: ${iconColor};
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: ${
        isSelected
          ? `0 0 0 5px ${bgColor}50, 0 0 0 11px ${bgColor}25, 0 8px 24px rgba(0,0,0,0.45)`
          : "0 3px 8px rgba(0,0,0,0.3)"
      };
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    ">
      <span class="geom-svg-icon" style="width:${iconSize}px; height:${iconSize}px; display:inline-flex; align-items:center; justify-content:center; color:inherit;">
        ${style.icon}
      </span>
      ${approxDotHtml}
    </div>
  `;

  return L.divIcon({
    className: `category-pin-${isSelected ? "selected" : "normal"}`,
    html: svgHtml,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Retorna uma instância cacheada de L.divIcon para a combinação de categoria/aproximado/selecionado.
 * Garante referência estável evitando que o React-Leaflet invoque marker.setIcon() desnecessariamente.
 *
 * @param {object} params
 * @param {string} params.categoryName
 * @param {boolean} params.isApproximate
 * @param {boolean} params.isSelected
 * @returns {L.DivIcon}
 */
function getCachedCategoryPinIcon({ categoryName, isApproximate, isSelected }) {
  const cat = categoryName || "Outros";
  const key = `${cat}_${isApproximate ? "1" : "0"}_${isSelected ? "1" : "0"}`;
  let icon = pinIconCache.get(key);
  if (!icon) {
    icon = createCategoryPinIcon({ categoryName: cat, isApproximate, isSelected });
    pinIconCache.set(key, icon);
  }
  return icon;
}

/**
 * Componente interno para escutar redimensionamentos de janela e atualizar o mapa.
 */
function MapResizeHandler() {
  const map = useMap();
  useEffect(() => {
    const handleResize = () => {
      map.invalidateSize();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [map]);
  return null;
}

/**
 * Escuta cliques no fundo do mapa para desativar a seleção ativa, respeitando a trava do spiderfy.
 */
function MapClickHandler({ onMapClick, clusterGroupRef }) {
  useMapEvents({
    click: () => {
      const cg = clusterGroupRef?.current;
      if (cg?._spiderfyLockUntil && Date.now() < cg._spiderfyLockUntil) {
        return;
      }
      onMapClick();
    },
  });
  return null;
}

/**
 * Tenta acionar o spiderfy em um cluster de mesma localização de forma resiliente, aguardando
 * o término de animações internas de zoom do Leaflet.MarkerCluster e aplicando trava temporal.
 *
 * @param {any} cluster
 * @param {any} clusterGroup
 * @returns {void}
 */
function trySpiderfyCluster(cluster, clusterGroup) {
  if (!cluster || !clusterGroup) return;
  // Se este cluster já está spiderfied, não faz nada
  const isAlreadySpiderfied =
    clusterGroup._spiderfiedSet?.has(cluster) ||
    clusterGroup._spiderfied === cluster;
  if (isAlreadySpiderfied) return;

  let attempts = 0;
  const maxAttempts = 8;

  const execute = () => {
    attempts++;
    if (!clusterGroup._map) return;

    // Se já foi spiderfied enquanto aguardava, não reexecuta
    if (
      clusterGroup._spiderfiedSet?.has(cluster) ||
      clusterGroup._spiderfied === cluster
    ) {
      return;
    }

    // Se o MarkerClusterGroup ainda estiver em animação interna de zoom, aguarda e tenta de novo
    if (clusterGroup._inZoomAnimation) {
      if (attempts < maxAttempts) {
        setTimeout(execute, 70);
      }
      return;
    }

    try {
      if (typeof cluster.spiderfy === "function") {
        clusterGroup._spiderfyLockUntil = Date.now() + 1000;
        clusterGroup._lastSpiderfyTime = Date.now();
        cluster.spiderfy();
      }
    } catch (err) {
      console.warn("Aviso ao expandir spiderfy no cluster:", err);
    }
  };

  execute();
}

/**
 * Garante que qualquer aranha aberta (spiderfy), hastes (legs) e botões de fechamento
 * sejam imediatamente recolhidos e removidos do Leaflet quando o modo Heatmap for ativado.
 */
function SpiderfyHeatmapSync({ isHeatmapActive, clusterGroupRef }) {
  const map = useMap();

  useEffect(() => {
    if (!isHeatmapActive) return;

    const cg = clusterGroupRef?.current;
    if (cg) {
      delete cg._spiderfyLockUntil;
      if (cg._spiderfiedSet && cg._spiderfiedSet.size > 0) {
        Array.from(cg._spiderfiedSet).forEach((c) => {
          try {
            c._noanimationUnspiderfy();
          } catch (e) {
            logBestEffortCleanupError(e);
          }
        });
        cg._spiderfiedSet.clear();
        cg._spiderfied = null;
      } else if (cg._spiderfied) {
        try {
          cg._spiderfied._noanimationUnspiderfy();
        } catch (e) {
          logBestEffortCleanupError(e);
        }
        cg._spiderfied = null;
      }
    }

    if (map) {
      map.eachLayer((layer) => {
        if (layer._spiderLeg) {
          try {
            map.removeLayer(layer._spiderLeg);
          } catch (e) {
            logBestEffortCleanupError(e);
          }
          delete layer._spiderLeg;
        }
        if (
          layer instanceof L.Polyline &&
          (layer.options?.className === "cluster-spider-leg" ||
            layer.options?.dashArray === "4, 4" ||
            layer._path?.classList?.contains("cluster-spider-leg") ||
            layer._path?.classList?.contains("leaflet-cluster-spider-leg"))
        ) {
          try {
            map.removeLayer(layer);
          } catch (e) {
            logBestEffortCleanupError(e);
          }
        }
      });

      const container = map.getContainer();
      if (container) {
        container.querySelectorAll(".cluster-close-btn").forEach((el) => el.remove());
        container.querySelectorAll(".cluster-is-spiderfied").forEach((el) => {
          el.classList.remove("cluster-is-spiderfied");
        });
      }
    }
  }, [isHeatmapActive, map, clusterGroupRef]);

  return null;
}

/**
 * Detecta quando o usuário dá zoom em direção a um ponto com múltiplas indicações
 * na MESMA localização geográfica e expande automaticamente o spiderfy.
 *
 * NUNCA aciona spiderfy em clusters compostos por coordenadas diferentes (esses se
 * separam naturalmente com o aumento do zoom).
 *
 * @param {{
 *   clusterGroupRef: React.RefObject<any>,
 *   clusterGroupInstance?: any,
 *   selectedId?: number|null,
 *   isHeatmapActive?: boolean
 * }} props
 */
function AutoSpiderfyOnZoom({
  clusterGroupRef,
  clusterGroupInstance,
  selectedId,
  isHeatmapActive = false,
}) {
  const map = useMap();
  const timerRef = useRef(null);
  const prevZoomRef = useRef(map.getZoom());

  // Cancela qualquer timer pendente caso o modo heatmap seja ativado
  useEffect(() => {
    if (isHeatmapActive && timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, [isHeatmapActive]);

  useMapEvents({
    zoomend: () => {
      if (isHeatmapActive) return;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      const currentZoom = map.getZoom();
      const prevZoom = prevZoomRef.current;
      prevZoomRef.current = currentZoom;

      // Se o usuário está afastando o zoom (zoom out de volta): recolhe automaticamente todos os spiderfies abertos!
      if (currentZoom < prevZoom) {
        const clusterGroup = clusterGroupInstance || clusterGroupRef?.current;
        if (clusterGroup) {
          delete clusterGroup._spiderfyLockUntil;
          if (clusterGroup._spiderfiedSet && clusterGroup._spiderfiedSet.size > 0) {
            const all = Array.from(clusterGroup._spiderfiedSet);
            all.forEach((c) => {
              try {
                c._noanimationUnspiderfy();
              } catch (e) {
                logBestEffortCleanupError(e);
              }
            });
            clusterGroup._spiderfiedSet.clear();
            clusterGroup._spiderfied = null;
          } else if (clusterGroup._spiderfied) {
            try {
              clusterGroup._spiderfied._noanimationUnspiderfy();
            } catch (e) {
              logBestEffortCleanupError(e);
            }
            clusterGroup._spiderfied = null;
          }
        }
        return;
      }

      // Se não houve alteração no nível de zoom (ex: simples arrasto/pan), não auto-expande
      if (currentZoom === prevZoom) return;

      // Auto-expande a partir do zoom 13.5 (aproximação inicial da cidade/área central)
      if (currentZoom < 13.5) return;

      timerRef.current = setTimeout(() => {
        const clusterGroup = clusterGroupInstance || clusterGroupRef?.current;
        if (!clusterGroup || !clusterGroup._featureGroup) return;

        const mapBounds = map.getBounds();
        const mapCenter = map.getCenter();
        const samePointClusters = [];

        clusterGroup._featureGroup.eachLayer((layer) => {
          if (
            typeof layer.getAllChildMarkers === "function" &&
            typeof layer.spiderfy === "function"
          ) {
            const clusterLatLng = layer.getLatLng();
            if (mapBounds.contains(clusterLatLng)) {
              const childMarkers = layer.getAllChildMarkers();
              // Regra estrita: apenas clusters cujos marcadores estão na MESMA localização física
              if (childMarkers.length > 1 && areMarkersAtSameLocation(childMarkers, layer)) {
                const containsSelected = selectedId
                  ? childMarkers.some((m) => m.options?.indicacaoId === selectedId)
                  : false;

                samePointClusters.push({
                  cluster: layer,
                  count: childMarkers.length,
                  containsSelected,
                  distToCenter: mapCenter.distanceTo(clusterLatLng),
                });
              }
            }
          }
        });

        // Se não há nenhum cluster de mesma localização na tela, não faz nada
        if (samePointClusters.length === 0) return;

        // Ordena priorizando o cluster que contém a indicação selecionada, seguido da proximidade ao centro da tela
        samePointClusters.sort((a, b) => {
          if (a.containsSelected && !b.containsSelected) return -1;
          if (!a.containsSelected && b.containsSelected) return 1;
          return a.distToCenter - b.distToCenter;
        });

        const openClusters = clusterGroup._spiderfiedSet
          ? Array.from(clusterGroup._spiderfiedSet)
          : clusterGroup._spiderfied
          ? [clusterGroup._spiderfied]
          : [];

        const candidatesToOpen = [];

        for (const item of samePointClusters) {
          const cluster = item.cluster;
          // Ignora se este cluster já está aberto
          if (
            cluster._isSpiderfied ||
            clusterGroup._spiderfiedSet?.has(cluster) ||
            clusterGroup._spiderfied === cluster
          ) {
            continue;
          }

          const pt = map.latLngToLayerPoint(cluster.getLatLng());

          // Verifica se colide com algum cluster já aberto (< 120px na tela)
          const collidesWithOpen = openClusters.some((oc) => {
            if (typeof oc.getLatLng !== "function") return false;
            return pt.distanceTo(map.latLngToLayerPoint(oc.getLatLng())) < SPIDERFY_COLLISION_DISTANCE_PX;
          });
          if (collidesWithOpen) continue;

          // Verifica se colide com outro candidato selecionado nesta mesma rodada (< 120px na tela)
          const collidesWithCandidates = candidatesToOpen.some((cand) => {
            return pt.distanceTo(map.latLngToLayerPoint(cand.getLatLng())) < SPIDERFY_COLLISION_DISTANCE_PX;
          });
          if (collidesWithCandidates) continue;

          candidatesToOpen.push(cluster);
          // Limite seguro de até 4 clusters simultâneos por rodada para fluidez
          if (candidatesToOpen.length >= 4) break;
        }

        candidatesToOpen.forEach((cluster, idx) => {
          setTimeout(() => {
            trySpiderfyCluster(cluster, clusterGroup);
          }, idx * 60);
        });
      }, 350);
    },
  });

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return null;
}

/**
/**
 * Ajusta o viewport do mapa com compensação de viewport para o painel lateral de 380px
 * e aciona desempacotamento/spiderfy nativo em clusters quando necessário.
 *
 * @param {{
 *   pins: import("../store/indicacoesSlice").GeoPin[],
 *   selectedId?: number|null,
 *   isGeometryVisible?: boolean,
 *   focusMapTrigger?: number,
 *   clusterGroupRef?: React.RefObject<any>,
 *   markerMapRef?: React.RefObject<Map<number, any>>
 * }} props
 */
function FitBounds({
  pins,
  selectedId,
  selectedPin = null,
  richGeometries = [],
  isGeometryVisible = false,
  focusMapTrigger = 0,
  clusterGroupRef,
  markerMapRef,
  statsPanelCollapsed = true,
}) {
  const map = useMap();
  const prevPinsRef = useRef(null);
  const prevSelectedRef = useRef(null);
  const prevTriggerRef = useRef(0);
  const prevGeomVisibleRef = useRef(isGeometryVisible);
  const prevGeomCountRef = useRef(0);

  useEffect(() => {
    const safePins = pins || [];
    const validCoords = safePins
      .filter((p) => p && typeof p.latitude === "number" && typeof p.longitude === "number")
      .map((p) => [p.latitude, p.longitude]);

    if (validCoords.length === 0) return;

    const isSelectionChanged = selectedId && selectedId !== prevSelectedRef.current;
    const isTriggerFired = focusMapTrigger && focusMapTrigger !== prevTriggerRef.current;
    const isGeomToggleChanged = isGeometryVisible !== prevGeomVisibleRef.current;
    const isGeomNewlyLoaded =
      isGeometryVisible &&
      richGeometries.length > 0 &&
      prevGeomCountRef.current === 0;

    // Se houve seleção de um pin específico, disparo de foco, toggle ou chegada de geometria
    if (selectedId && (isSelectionChanged || isTriggerFired || isGeomToggleChanged || isGeomNewlyLoaded)) {
      const activePin =
        selectedPin ||
        safePins.find((p) => p.indicacao_id === selectedId);

      if (activePin) {
        const isDesktop = window.innerWidth >= 900;
        const padLeft = isDesktop ? 410 : 30;
        const padRight = isDesktop ? (statsPanelCollapsed ? 60 : 350) : 30;
        const padTop = 60;
        const padBottom = 60;

        // 1. Se a geometria estiver visível e houver geometrias vetoriais ricas
        if (isGeometryVisible && richGeometries.length > 0) {
          try {
            const group = L.featureGroup();
            richGeometries.forEach((g) => {
              if (g.geometry_geojson) {
                const layer = L.geoJSON(g.geometry_geojson);
                group.addLayer(layer);
              }
            });
            const geoBounds = group.getBounds();
            if (geoBounds.isValid()) {
              map.fitBounds(geoBounds, {
                paddingTopLeft: [padLeft, padTop],
                paddingBottomRight: [padRight, padBottom],
                maxZoom: 17,
                duration: 0.8,
              });
              prevSelectedRef.current = selectedId;
              prevTriggerRef.current = focusMapTrigger;
              prevGeomVisibleRef.current = isGeometryVisible;
              prevGeomCountRef.current = richGeometries.length;
              return;
            }
          } catch (err) {
            console.warn("Erro ao calcular bounds de geometria GeoJSON:", err);
          }
        }

        // 2. Foco no marcador usando zoomToShowLayer (desempacota clusters e aciona spiderfy nativo)
        const marker = markerMapRef?.current?.get(selectedId);
        const clusterGroup = clusterGroupRef?.current;

        // Se o marcador já estiver visível dentro de qualquer cluster spiderfied ativo,
        // NÃO chama zoomToShowLayer para não recolher a aranha nem forçar re-spiderfy!
        const parentCluster = marker?.__parent;
        const spiderfiedSet = clusterGroup?._spiderfiedSet;
        const isMarkerAlreadySpiderfied = Boolean(
          parentCluster?._isSpiderfied ||
          (spiderfiedSet && spiderfiedSet.size > 0 && Array.from(spiderfiedSet).some(
            (c) =>
              c === parentCluster ||
              (typeof c.getAllChildMarkers === "function" &&
                c.getAllChildMarkers().some(
                  (m) => m === marker || m.options?.indicacaoId === selectedId
                ))
          )) ||
          (clusterGroup?._spiderfied &&
            (clusterGroup._spiderfied === parentCluster ||
              (typeof clusterGroup._spiderfied.getAllChildMarkers === "function" &&
                clusterGroup._spiderfied.getAllChildMarkers().some(
                  (m) => m === marker || m.options?.indicacaoId === selectedId
                ))))
        );

        if (isMarkerAlreadySpiderfied) {
          if (isTriggerFired) {
            const targetLatLng =
              marker && typeof marker.getLatLng === "function"
                ? marker.getLatLng()
                : [activePin.latitude, activePin.longitude];
            const pointBounds = L.latLngBounds([targetLatLng, targetLatLng]);
            try {
              map.fitBounds(pointBounds, {
                maxZoom: map.getZoom(),
                paddingTopLeft: [padLeft, padTop],
                paddingBottomRight: [padRight, padBottom],
                duration: 0.5,
              });
            } catch (err) {
              console.warn("fitBounds failed on spiderfied marker:", err);
            }
          }
          prevSelectedRef.current = selectedId;
          prevTriggerRef.current = focusMapTrigger;
          prevGeomVisibleRef.current = isGeometryVisible;
          prevGeomCountRef.current = richGeometries.length;
          return;
        }

        if (
          marker &&
          clusterGroup &&
          clusterGroup._map &&
          typeof clusterGroup.zoomToShowLayer === "function" &&
          typeof map.getZoom === "function"
        ) {
          try {
            clusterGroup.zoomToShowLayer(marker, () => {
              const targetLatLng =
                marker && typeof marker.getLatLng === "function"
                  ? marker.getLatLng()
                  : [activePin.latitude, activePin.longitude];
              const pointBounds = L.latLngBounds([targetLatLng, targetLatLng]);
              try {
                map.fitBounds(pointBounds, {
                  maxZoom: Math.max(map.getZoom(), 16.5),
                  paddingTopLeft: [padLeft, padTop],
                  paddingBottomRight: [padRight, padBottom],
                  duration: 0.5,
                });
              } catch (e) {
                console.warn("fitBounds after zoomToShowLayer failed:", e);
              }
            });
            prevSelectedRef.current = selectedId;
            prevTriggerRef.current = focusMapTrigger;
            prevGeomVisibleRef.current = isGeometryVisible;
            prevGeomCountRef.current = richGeometries.length;
            return;
          } catch (err) {
            console.warn("zoomToShowLayer failed, falling back to fitBounds:", err);
          }
        }

        // 3. Fallback: foco no ponto específico do pin com compensação de tela
        if (typeof activePin.latitude === "number" && typeof activePin.longitude === "number") {
          const pointBounds = L.latLngBounds([
            [activePin.latitude, activePin.longitude],
            [activePin.latitude, activePin.longitude],
          ]);
          map.fitBounds(pointBounds, {
            maxZoom: 16.5,
            paddingTopLeft: [padLeft, padTop],
            paddingBottomRight: [padRight, padBottom],
            duration: 0.8,
          });
          prevSelectedRef.current = selectedId;
          prevTriggerRef.current = focusMapTrigger;
          prevGeomVisibleRef.current = isGeometryVisible;
          prevGeomCountRef.current = richGeometries.length;
          return;
        }
      }
    }

    prevSelectedRef.current = selectedId;
    prevTriggerRef.current = focusMapTrigger;
    prevGeomVisibleRef.current = isGeometryVisible;
    prevGeomCountRef.current = richGeometries.length;

    // Reset geral de limites quando muda a lista de pins e não há pin selecionado
    if (!selectedId) {
      const pinFingerprint = `${safePins.length}_${safePins[0]?.id || 0}_${safePins[safePins.length - 1]?.id || 0}`;
      if (prevPinsRef.current === pinFingerprint) return;

      const bounds = L.latLngBounds(validCoords);

      if (bounds.isValid()) {
        const timer = setTimeout(() => {
          try {
            map.invalidateSize();
            map.fitBounds(bounds, { padding: [45, 45], maxZoom: 16 });
          } catch (e) {
            console.error("Erro ao ajustar limites do mapa:", e);
          }
        }, 100);
        prevPinsRef.current = pinFingerprint;
        return () => clearTimeout(timer);
      }
    }
  }, [
    pins,
    selectedId,
    selectedPin,
    richGeometries,
    isGeometryVisible,
    focusMapTrigger,
    map,
    clusterGroupRef,
    markerMapRef,
  ]);

  return null;
}

/**
 * Marcador individual de Pin memoizado para escalabilidade.
 * Previne re-renderizações e reconstruções desnecessárias no VDOM quando outros pins são selecionados.
 */
const PinMarker = memo(function PinMarker({
  pin,
  isSelected,
  onSelect,
  clusterGroupRef,
  markerMapRef,
}) {
  const categoryName =
    pin.thematic_category ||
    pin.indicacao?.thematic_category ||
    pin.indicacao?.categoria?.nome ||
    pin.categoria?.nome ||
    "Outros";

  const isRichGeom = pin.geometry_type && pin.geometry_type !== "Point";
  const geomMeta = getGeometryMetadata(pin.geometry_type);

  const icon = getCachedCategoryPinIcon({
    categoryName,
    isApproximate: pin.is_approximate,
    isSelected,
  });

  const position = pin._pos || (pin._pos = [pin.latitude, pin.longitude]);

  return (
    <Marker
      position={position}
      categoryName={categoryName}
      indicacaoId={pin.indicacao_id}
      ref={(m) => {
        if (m) {
          markerMapRef.current?.set(pin.indicacao_id, m);
        } else {
          markerMapRef.current?.delete(pin.indicacao_id);
        }
      }}
      icon={icon}
      eventHandlers={{
        click: (e) => {
          if (e?.originalEvent) {
            L.DomEvent.stopPropagation(e.originalEvent);
            L.DomEvent.preventDefault(e.originalEvent);
            e.originalEvent._stopped = true;
          }
          const clusterGroup = clusterGroupRef?.current;
          if (clusterGroup) {
            clusterGroup._spiderfyLockUntil = Date.now() + 1500;
            clusterGroup._lastSpiderfyTime = Date.now();
          }
          onSelect(pin.indicacao_id);
        },
      }}
    >
      <Tooltip direction="top" offset={[0, -10]} opacity={0.98}>
        <div
          style={{
            fontWeight: 700,
            fontSize: "var(--text-small)",
            color: "var(--text-primary)",
            marginBottom: 3,
            lineHeight: "var(--leading-snug)",
          }}
        >
          Indicação nº {pin.indicacao?.numero || pin.indicacao_id}
          {pin.indicacao?.ano ? `/${pin.indicacao.ano}` : ""}
        </div>
        <div
          style={{
            fontSize: "var(--text-label)",
            color: "var(--text-secondary)",
            lineHeight: "var(--leading-snug)",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {pin.normalized_address || pin.raw_address}
        </div>
        {isRichGeom && (
          <div
            style={{
              fontSize: "var(--text-micro)",
              color: "var(--accent-primary)",
              marginTop: 4,
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 600,
            }}
          >
            <GeometryIcon icon={geomMeta.icon} size={13} /> {geomMeta.label}
          </div>
        )}
        {pin.is_approximate && (
          <div
            style={{
              fontSize: "var(--text-micro)",
              color: "var(--status-approx-fg, #b45309)",
              marginTop: 4,
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 600,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>Endereço aproximado</span>
          </div>
        )}
      </Tooltip>
    </Marker>
  );
});

/**
 * Componente do Mapa Interativo com Leaflet e Clustering.
 *
 * @param {{ isDarkMode?: boolean, activeLayers?: { heatmap?: boolean } }} props
 * @returns {JSX.Element}
 */
export default function MapView({
  isDarkMode = false,
  activeLayers = {},
  onSelectLayer,
  onToggleHeatmap,
  onToggleChoropleth,
  statsPanelCollapsed = true,
  isMobile = false,
}) {
  const { pins, cityPins, loading, error } = usePins();
  const { selected, select, clear, isGeometryVisible, focusMapTrigger } = useSelectedIndicacao();
  const { filters, setBairro } = useFilters();

  const isHeatmapActive = Boolean(activeLayers?.heatmap);
  const isChoroplethActive = Boolean(activeLayers?.choropleth);
  const [choroplethMeta, setChoroplethMeta] = useState(null);

  const clusterGroupRef = useRef(null);
  const [clusterGroupInstance, setClusterGroupInstance] = useState(null);
  const markerMapRef = useRef(new Map());

  // Destaque visual leve de bairros oficiais do GPKG (mapa_abairramento)
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const ignoreNextMapClickRef = useRef(false);

  // Bairro ativo consolidado (do filtro global ou do clique no mapa)
  const activeNeighborhood = useMemo(() => {
    if (filters.bairro_id || filters.bairro) {
      const matchId = filters.bairro_id && selectedNeighborhood?.id === filters.bairro_id;
      const matchExact = matchId || (filters.bairro && selectedNeighborhood?.name?.toLowerCase() === filters.bairro.toLowerCase());
      return {
        id: filters.bairro_id || (matchExact ? selectedNeighborhood.id : null),
        name: canonicalizeBairro(filters.bairro) || selectedNeighborhood?.name || "Bairro",
        fullName: matchExact ? selectedNeighborhood.fullName : (canonicalizeBairro(filters.bairro) || "Bairro"),
      };
    }
    return selectedNeighborhood;
  }, [filters.bairro, filters.bairro_id, selectedNeighborhood]);

  const handleSelectNeighborhood = useCallback(
    (neighborhood) => {
      ignoreNextMapClickRef.current = true;
      if (!neighborhood) {
        setSelectedNeighborhood(null);
        if (filters.bairro || filters.bairro_id) setBairro(null);
        return;
      }
      const currentActiveId = filters.bairro_id || selectedNeighborhood?.id;
      const clickedId = neighborhood.id;
      const currentActiveName = (filters.bairro || selectedNeighborhood?.name || "").trim().toLowerCase();
      const clickedName = (neighborhood.name || "").trim().toLowerCase();
      const clickedFullName = (neighborhood.fullName || "").trim().toLowerCase();

      // Se clicou no bairro que já está ativo, desmarca e limpa o filtro
      const isAlreadyActive =
        (currentActiveId && clickedId && currentActiveId === clickedId) ||
        (currentActiveName && (currentActiveName === clickedName || currentActiveName === clickedFullName));

      if (isAlreadyActive) {
        setSelectedNeighborhood(null);
        setBairro(null);
      } else {
        setSelectedNeighborhood(neighborhood);
        setBairro(neighborhood.name, neighborhood.id);
      }
    },
    [filters.bairro, filters.bairro_id, selectedNeighborhood, setBairro]
  );

  // Estado de resiliência da camada de tiles (fallback automático em caso de falha do provedor primário)
  const [useFallbackTiles, setUseFallbackTiles] = useState(false);
  const [tileLayerUnavailable, setTileLayerUnavailable] = useState(false);
  const tileHealthRef = useRef({
    useFallbackTiles: false,
    tileLayerUnavailable: false,
    consecutiveErrors: 0,
  });

  const tileConfig = useMemo(
    () => getTileLayerConfig({ isDarkMode, useFallback: useFallbackTiles }),
    [isDarkMode, useFallbackTiles]
  );

  useEffect(() => {
    tileHealthRef.current = reduceTileLayerHealth(
      {
        useFallbackTiles,
        tileLayerUnavailable,
        consecutiveErrors: 0,
      },
      { type: "reset" },
      {
        hasFallback: tileConfig.hasFallback,
        errorTileUrl: tileConfig.errorTileUrl,
        threshold: TILE_ERROR_FALLBACK_THRESHOLD,
      }
    );
  }, [tileConfig.key, tileConfig.hasFallback, tileConfig.errorTileUrl, useFallbackTiles, tileLayerUnavailable]);

  const handleTileLoad = useCallback(
    (e) => {
      if (e?.target && !e.target._map) {
        return;
      }
      const loadedSrc = e?.tile?.getAttribute?.("src") || e?.tile?.src || e?.url || "";
      if (isErrorTileSource(loadedSrc, tileConfig.errorTileUrl)) {
        return;
      }
      const next = reduceTileLayerHealth(
        tileHealthRef.current,
        { type: "tileload", src: loadedSrc },
        {
          hasFallback: tileConfig.hasFallback,
          errorTileUrl: tileConfig.errorTileUrl,
          threshold: TILE_ERROR_FALLBACK_THRESHOLD,
        }
      );
      tileHealthRef.current = next;
      setTileLayerUnavailable((prev) => (prev !== next.tileLayerUnavailable ? next.tileLayerUnavailable : prev));
    },
    [tileConfig.errorTileUrl, tileConfig.hasFallback]
  );

  const handleTileError = useCallback(
    (e) => {
      if (e?.target && !e.target._map) {
        return;
      }
      const next = reduceTileLayerHealth(
        tileHealthRef.current,
        { type: "tileerror" },
        {
          hasFallback: tileConfig.hasFallback,
          errorTileUrl: tileConfig.errorTileUrl,
          threshold: TILE_ERROR_FALLBACK_THRESHOLD,
        }
      );
      tileHealthRef.current = next;
      if (next.useFallbackTiles && !useFallbackTiles) {
        setUseFallbackTiles(true);
      }
      if (next.tileLayerUnavailable) {
        setTileLayerUnavailable(true);
      }
    },
    [useFallbackTiles, tileConfig.hasFallback, tileConfig.errorTileUrl]
  );

  const tileEventHandlers = useMemo(
    () => ({
      tileload: handleTileLoad,
      tileerror: handleTileError,
    }),
    [handleTileLoad, handleTileError]
  );

  const handleMapClick = () => {
    if (ignoreNextMapClickRef.current) {
      ignoreNextMapClickRef.current = false;
      return;
    }
    if (selected?.id) {
      clear();
    }
    if (selectedNeighborhood || filters.bairro) {
      setSelectedNeighborhood(null);
      if (filters.bairro) {
        setBairro(null);
      }
    }
  };

  // Todos os pins com coordenadas válidas e reais (sem deslocamentos artificiais)
  const basePins = useMemo(() => {
    return (pins || []).filter(
      (p) => p && typeof p.latitude === "number" && typeof p.longitude === "number"
    );
  }, [pins]);

  // Pins do recorte global (pré-filtro geográfico de bairro) para o mapa coroplético
  const choroplethSourcePins = useMemo(() => {
    return Array.isArray(cityPins) && cityPins.length > 0 ? cityPins : pins || [];
  }, [cityPins, pins]);

  // Contagem de indicações únicas no bairro filtrado para o badge superior
  const activeNeighborhoodIndicacoesCount = useMemo(() => {
    if (
      isChoroplethActive &&
      activeNeighborhood?.id &&
      choroplethMeta?.statsById?.[Number(activeNeighborhood.id)]
    ) {
      return choroplethMeta.statsById[Number(activeNeighborhood.id)].count;
    }
    const ids = new Set();
    for (const p of basePins) {
      const id = p.indicacao_id ?? p.indicacao?.id ?? p.id;
      if (id !== null && id !== undefined) ids.add(id);
    }
    return ids.size;
  }, [isChoroplethActive, activeNeighborhood?.id, choroplethMeta, basePins]);

  // Pin ativo/selecionado com geometria rica mesclada
  const selectedPin = useMemo(() => {
    if (!selected?.id) return null;

    // 1. Localiza pin base (do mapa / clustering)
    const pinFromBase = basePins.find((p) => p.indicacao_id === selected.id);

    // 2. Localiza pin com geometria rica vindo do detalhe selecionado (selected)
    const richPinFromSelected =
      selected.geo_pins?.find(
        (p) => p.geometry_geojson && p.geometry_type && p.geometry_type !== "Point" && p.geometry_type !== "MultiPoint"
      ) ||
      selected.enderecos_extraidos?.find(
        (e) =>
          e.geo_pin?.geometry_geojson &&
          e.geo_pin?.geometry_type &&
          e.geo_pin?.geometry_type !== "Point" &&
          e.geo_pin?.geometry_type !== "MultiPoint"
      )?.geo_pin;

    // 3. Fallback para qualquer pin com geometry_geojson ou primeiro pin disponível
    const pinFromSelected =
      richPinFromSelected ||
      selected.geo_pins?.find((p) => p.geometry_geojson) ||
      selected.enderecos_extraidos?.find((e) => e.geo_pin?.geometry_geojson)?.geo_pin ||
      selected.geo_pins?.[0] ||
      selected.enderecos_extraidos?.find((e) => e.geo_pin)?.geo_pin;

    if (!pinFromBase && !pinFromSelected) return null;

    const baseObj = pinFromBase || {};
    const selectedObj = pinFromSelected || {};

    return {
      ...baseObj,
      ...selectedObj,
      // Garante explicitamente os campos críticos de geometria GeoJSON
      geometry_geojson: selectedObj.geometry_geojson || baseObj.geometry_geojson || null,
      geometry_type: selectedObj.geometry_type || baseObj.geometry_type || null,
      latitude:
        typeof selectedObj.latitude === "number"
          ? selectedObj.latitude
          : baseObj.latitude,
      longitude:
        typeof selectedObj.longitude === "number"
          ? selectedObj.longitude
          : baseObj.longitude,
      indicacao: selectedObj.indicacao || baseObj.indicacao || {
        id: selected.id,
        thematic_category: selected.thematic_category,
        numero: selected.numero,
        ano: selected.ano,
        ementa: selected.ementa,
        autor: selected.autor,
      },
    };
  }, [basePins, selected]);

  // Lista de todas as feições com geometria rica (LineString, Polygon) associadas à indicação selecionada
  const richGeometries = useMemo(() => {
    if (!selected?.id || !isGeometryVisible) return [];

    const list = [];
    const seenGeoPinIds = new Set();

    const addPin = (pin) => {
      if (
        pin &&
        pin.geometry_geojson &&
        pin.geometry_type &&
        pin.geometry_type !== "Point" &&
        pin.geometry_type !== "MultiPoint"
      ) {
        const key = pin.id || `${pin.indicacao_id}-${pin.geometry_type}`;
        if (!seenGeoPinIds.has(key)) {
          seenGeoPinIds.add(key);
          list.push(pin);
        }
      }
    };

    if (Array.isArray(selected.geo_pins)) {
      selected.geo_pins.forEach(addPin);
    }
    if (Array.isArray(selected.enderecos_extraidos)) {
      selected.enderecos_extraidos.forEach((e) => addPin(e?.geo_pin));
    }

    if (selectedPin && selectedPin.geometry_geojson) {
      addPin(selectedPin);
    }

    return list;
  }, [selected, isGeometryVisible, selectedPin]);

  // Mantém SEMPRE as coordenadas geográficas autênticas para clustering inteligente e spiderfy nativo
  const displayPins = basePins;

  // Cria a função de ícone de cluster memoizada (visual neutro e consistente para clusters)
  const createClusterIcon = useCallback((cluster) => {
    return createClusterCustomIcon(cluster);
  }, []);

  // Intercepta e gerencia múltiplos spiderfies simultâneos com blindagem contra colisões e fechamentos acidentais
  useEffect(() => {
    const cg = clusterGroupInstance || clusterGroupRef.current;
    if (!cg) return;

    if (!cg._spiderfiedSet) {
      cg._spiderfiedSet = new Set();
    }

    // Registra listener para manter o Set sincronizado com as aberturas e fechamentos de aranha
    if (!cg._spiderfyListenersAttached) {
      cg._spiderfyListenersAttached = true;

      cg.on("spiderfied", (e) => {
        if (e?.cluster) {
          e.cluster._isSpiderfied = true;
          cg._spiderfiedSet.add(e.cluster);
          cg._spiderfied = e.cluster;
          if (e.cluster._icon) {
            L.DomUtil.addClass(e.cluster._icon, "cluster-is-spiderfied");
            e.cluster._icon.style.display = "";
            e.cluster._icon.style.opacity = "1";
            e.cluster.setOpacity(1);

            // Vincula listener direto no botão central para recolher de imediato
            const closeBtn = e.cluster._icon.querySelector(".cluster-close-btn");
            if (closeBtn) {
              L.DomEvent.disableClickPropagation(closeBtn);
              closeBtn.onclick = (evt) => {
                if (evt) {
                  evt.stopPropagation();
                  evt.preventDefault();
                  if (evt.originalEvent) evt.originalEvent._stopped = true;
                }
                delete cg._spiderfyLockUntil;
                e.cluster.unspiderfy();
              };
            }
          }
        }
      });

      cg.on("unspiderfied", (e) => {
        if (e?.cluster) {
          e.cluster._isSpiderfied = false;
          cg._spiderfiedSet.delete(e.cluster);
          cg._spiderfied =
            cg._spiderfiedSet.size > 0 ? Array.from(cg._spiderfiedSet)[0] : null;
          if (e.cluster._icon) {
            L.DomUtil.removeClass(e.cluster._icon, "cluster-is-spiderfied");
            e.cluster._icon.style.display = "";
            e.cluster._icon.style.opacity = "1";
            e.cluster.setOpacity(1);
          }
        }
      });
    }

    // Patch pontual no método spiderfy do MarkerCluster para suporte a múltiplos spiderfies com blindagem contra re-execução
    if (L.MarkerCluster && !L.MarkerCluster.prototype._multiSpiderfyPatched) {
      L.MarkerCluster.prototype._multiSpiderfyPatched = true;
      const originalSpiderfy = L.MarkerCluster.prototype.spiderfy;
      const originalUnspiderfy = L.MarkerCluster.prototype.unspiderfy;
      const originalNoanimationUnspiderfyCluster = L.MarkerCluster.prototype._noanimationUnspiderfy;

      L.MarkerCluster.prototype.spiderfy = function () {
        // Se este cluster específico já está spiderfied, NUNCA executa spiderfy novamente para não colapsar os pins no centro
        if (this._isSpiderfied) {
          return;
        }
        this._isSpiderfied = true;

        if (this._group) {
          if (!this._group._spiderfiedSet) this._group._spiderfiedSet = new Set();
          this._group._spiderfiedSet.add(this);
          this._group._spiderfyingCluster = this;
        }
        try {
          return originalSpiderfy.apply(this, arguments);
        } finally {
          if (this._group) {
            delete this._group._spiderfyingCluster;
          }
        }
      };

      L.MarkerCluster.prototype.unspiderfy = function () {
        this._isSpiderfied = false;
        if (this._group?._spiderfiedSet) {
          this._group._spiderfiedSet.delete(this);
        }
        return originalUnspiderfy.apply(this, arguments);
      };

      L.MarkerCluster.prototype._noanimationUnspiderfy = function () {
        this._isSpiderfied = false;
        if (this._group?._spiderfiedSet) {
          this._group._spiderfiedSet.delete(this);
        }
        return originalNoanimationUnspiderfyCluster.apply(this, arguments);
      };
    }

    // Garante limpeza integral de spiderfies se o MarkerClusterGroup for desmontado (ex: alternar para Heatmap)
    if (L.MarkerClusterGroup && !L.MarkerClusterGroup.prototype._cleanOnRemovePatched) {
      L.MarkerClusterGroup.prototype._cleanOnRemovePatched = true;
      const origOnRemove = L.MarkerClusterGroup.prototype.onRemove;
      L.MarkerClusterGroup.prototype.onRemove = function (map) {
        delete this._spiderfyLockUntil;
        if (this._spiderfiedSet && this._spiderfiedSet.size > 0) {
          const all = Array.from(this._spiderfiedSet);
          all.forEach((c) => {
            try {
              c._noanimationUnspiderfy();
            } catch (e) {
              logBestEffortCleanupError(e);
            }
          });
          this._spiderfiedSet.clear();
          this._spiderfied = null;
        } else if (this._spiderfied) {
          try {
            this._spiderfied._noanimationUnspiderfy();
          } catch (e) {
            logBestEffortCleanupError(e);
          }
          this._spiderfied = null;
        }
        return origOnRemove.apply(this, arguments);
      };
    }

    if (cg._spiderfyGuardInstalled) return;
    cg._spiderfyGuardInstalled = true;

    const originalUnspiderfyWrapper = cg._unspiderfyWrapper;
    const originalUnspiderfy = cg._unspiderfy;
    const originalNoanimationUnspiderfy = cg._noanimationUnspiderfy;

    cg._unspiderfyWrapper = function (...args) {
      if (this._spiderfyLockUntil && Date.now() < this._spiderfyLockUntil) {
        return;
      }
      // Clique no fundo do mapa: fecha todos os spiderfies abertos
      if (this._spiderfiedSet && this._spiderfiedSet.size > 0) {
        const all = Array.from(this._spiderfiedSet);
        all.forEach((c) => {
          try {
            c._noanimationUnspiderfy();
          } catch (e) {
            logBestEffortCleanupError(e);
          }
        });
        this._spiderfiedSet.clear();
        this._spiderfied = null;
        return;
      }
      return originalUnspiderfyWrapper?.apply(this, args);
    };

    cg._unspiderfy = function (...args) {
      if (this._spiderfyLockUntil && Date.now() < this._spiderfyLockUntil) {
        if (args[0] && args[0].zoom !== undefined && this._map && args[0].zoom !== this._map.getZoom()) {
          // Permite unspiderfy durante zoom real
        } else if (!this._spiderfyingCluster) {
          return;
        }
      }

      // Se esta chamada veio de dentro de cluster.spiderfy():
      if (this._spiderfyingCluster) {
        const callingCluster = this._spiderfyingCluster;
        const map = this._map;
        if (!this._spiderfiedSet) this._spiderfiedSet = new Set();

        if (map) {
          const callingPt = map.latLngToLayerPoint(callingCluster.getLatLng());
          const toClose = [];
          this._spiderfiedSet.forEach((openCluster) => {
            if (openCluster === callingCluster) return;
            if (typeof openCluster.getLatLng !== "function") return;
            const openPt = map.latLngToLayerPoint(openCluster.getLatLng());
            // Só fecha clusters que estejam muito perto (< 120px) para evitar colisão visual das pernas
            if (callingPt.distanceTo(openPt) < SPIDERFY_COLLISION_DISTANCE_PX) {
              toClose.push(openCluster);
            }
          });
          toClose.forEach((c) => {
            try {
              c.unspiderfy();
            } catch (e) {
              logBestEffortCleanupError(e);
            }
            this._spiderfiedSet.delete(c);
          });
        }
        // Clusters não colidentes permanecem abertos!
        return;
      }

      // Se veio de zoom ou chamada global sem _spiderfyingCluster: fecha todos os spiderfies abertos
      if (this._spiderfiedSet && this._spiderfiedSet.size > 0) {
        const all = Array.from(this._spiderfiedSet);
        all.forEach((c) => {
          try {
            c._noanimationUnspiderfy();
          } catch (e) {
            logBestEffortCleanupError(e);
          }
        });
        this._spiderfiedSet.clear();
        this._spiderfied = null;
        return;
      }

      return originalUnspiderfy?.apply(this, args);
    };

    cg._noanimationUnspiderfy = function (...args) {
      if (this._spiderfiedSet && this._spiderfiedSet.size > 0) {
        const all = Array.from(this._spiderfiedSet);
        all.forEach((c) => {
          try {
            c._noanimationUnspiderfy();
          } catch (e) {
            logBestEffortCleanupError(e);
          }
        });
        this._spiderfiedSet.clear();
        this._spiderfied = null;
        return;
      }
      return originalNoanimationUnspiderfy?.apply(this, args);
    };
  }, [clusterGroupInstance]);

  return (
    <div
      className={selected?.id ? "has-selected-pin" : ""}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      {/* Indicador de carregamento em formato Glassmorphism */}
      {loading && (
        <div
          className="glass-card"
          style={{
            position: "absolute",
            top: 14,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            padding: "6px 16px",
            fontSize: "var(--text-label)",
            fontWeight: 600,
            color: "var(--text-primary)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div className="skeleton" style={{ width: 8, height: 8, borderRadius: "50%" }} />
          Atualizando mapa...
        </div>
      )}

      {/* Mensagem de erro */}
      {error && (
        <div
          className="glass-card"
          style={{
            position: "absolute",
            top: 14,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            padding: "8px 16px",
            fontSize: "var(--text-label)",
            fontWeight: 600,
            background: "rgba(225, 29, 72, 0.15)",
            color: "#f43f5e",
            border: "1px solid rgba(225, 29, 72, 0.3)",
          }}
        >
          Erro ao carregar mapa: {error}
        </div>
      )}

      {/* Aviso discreto caso todos os provedores de tiles estejam indisponíveis */}
      {tileLayerUnavailable && !loading && (
        <div
          role="status"
          data-testid="tile-layer-warning"
          className="glass-card"
          style={{
            position: "absolute",
            bottom: isMobile ? 74 : 26,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 990,
            padding: "5px 12px",
            fontSize: "var(--text-micro)",
            fontWeight: 600,
            color: "var(--text-secondary)",
            background: "var(--bg-glass)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-full)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          Base cartográfica offline — exibindo indicações sobre plano neutro
        </div>
      )}

      {/* Badge flutuante indicando o bairro destacado e filtrado */}
      {activeNeighborhood && (
        <div
          role="status"
          aria-live="polite"
          data-testid="neighborhood-highlight-badge"
          className="glass-card fade-in"
          style={{
            position: "absolute",
            top: 14,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 990,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 14px",
            borderRadius: "var(--radius-full)",
            fontSize: "var(--text-small)",
            fontWeight: 600,
            color: "var(--text-primary)",
            background: "var(--bg-glass)",
            border: "1px solid var(--border-color)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            pointerEvents: "auto",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: isDarkMode ? "#38bdf8" : "#2563eb",
              boxShadow: isDarkMode ? "0 0 8px #38bdf8" : "0 0 6px #2563eb",
            }}
          />
          <span>{activeNeighborhood.fullName || activeNeighborhood.name}</span>
          {filters.bairro && (
            <span
              style={{
                fontSize: "var(--text-micro)",
                fontWeight: 500,
                color: "var(--text-muted)",
                marginLeft: 2,
              }}
            >
              ({activeNeighborhoodIndicacoesCount}{" "}
              {activeNeighborhoodIndicacoesCount === 1 ? "indicação" : "indicações"})
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              setSelectedNeighborhood(null);
              setBairro(null);
            }}
            title="Remover filtro do bairro"
            aria-label="Remover filtro do bairro"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0 2px",
              marginLeft: 4,
              color: "var(--text-secondary)",
              fontSize: 16,
              lineHeight: 1,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            &times;
          </button>
        </div>
      )}

      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ width: "100%", height: "100%" }}
        scrollWheelZoom
        zoomControl={false}
      >
        <MapResizeHandler />
        <MapClickHandler onMapClick={handleMapClick} clusterGroupRef={clusterGroupRef} />

        {tileConfig.isVector ? (
          <VectorBasemapLayer
            key={tileConfig.key}
            url={tileConfig.url}
            attribution={tileConfig.attribution}
            minZoom={tileConfig.minZoom}
            maxZoom={tileConfig.maxZoom}
            maxDataZoom={tileConfig.maxDataZoom}
            isDarkMode={isDarkMode}
            onTileLoad={handleTileLoad}
            onTileError={handleTileError}
          />
        ) : (
          <TileLayer
            key={tileConfig.key}
            attribution={tileConfig.attribution}
            url={tileConfig.url}
            subdomains={tileConfig.subdomains}
            minZoom={tileConfig.minZoom}
            maxZoom={tileConfig.maxZoom}
            maxNativeZoom={tileConfig.maxNativeZoom}
            errorTileUrl={tileConfig.errorTileUrl}
            className={tileConfig.className}
            eventHandlers={tileEventHandlers}
          />
        )}

        {/* Camada vetorial de bairros: Coroplético (5 faixas) ou Realce de Seleção */}
        {isChoroplethActive ? (
          <ChoroplethLayer
            cityPins={choroplethSourcePins}
            selectedNeighborhood={activeNeighborhood}
            onSelectNeighborhood={handleSelectNeighborhood}
            onChoroplethMetaChange={setChoroplethMeta}
            isDarkMode={isDarkMode}
          />
        ) : (
          <NeighborhoodHighlightLayer
            selectedNeighborhood={activeNeighborhood}
            onSelectNeighborhood={handleSelectNeighborhood}
            isDarkMode={isDarkMode}
          />
        )}

        <FitBounds
          pins={isChoroplethActive ? choroplethSourcePins : basePins}
          selectedId={selected?.id}
          selectedPin={selectedPin}
          richGeometries={richGeometries}
          isGeometryVisible={isGeometryVisible}
          focusMapTrigger={focusMapTrigger}
          clusterGroupRef={clusterGroupRef}
          markerMapRef={markerMapRef}
          statsPanelCollapsed={statsPanelCollapsed}
        />

        {/* Sincronizador de recolhimento e limpeza do spiderfy ao alternar para heatmap ou coroplético */}
        <SpiderfyHeatmapSync
          isHeatmapActive={isHeatmapActive || isChoroplethActive}
          clusterGroupRef={clusterGroupRef}
        />

        {/* Expansão automática do spiderfy ao aproximar o zoom em pontos com múltiplas indicações */}
        <AutoSpiderfyOnZoom
          clusterGroupRef={clusterGroupRef}
          clusterGroupInstance={clusterGroupInstance}
          selectedId={selected?.id}
          isHeatmapActive={isHeatmapActive || isChoroplethActive}
        />

        {/* Barra de Ferramentas Unificada do Mapa (Zoom, Localização, Seletor de Camadas e Legenda) */}
        <MapToolbar
          isHeatmapActive={isHeatmapActive}
          isChoroplethActive={isChoroplethActive}
          onSelectLayer={onSelectLayer}
          onToggleHeatmap={onToggleHeatmap}
          onToggleChoropleth={onToggleChoropleth}
          choroplethMeta={choroplethMeta}
          isDarkMode={isDarkMode}
          statsPanelCollapsed={statsPanelCollapsed}
          isMobile={isMobile}
        />

        {/* Camada GeoJSON vetorial destacada para o(s) pin(s) selecionado(s) quando isGeometryVisible for true */}
        {isGeometryVisible &&
          richGeometries.map((geomPin, idx) => {
            const catName =
              geomPin.indicacao?.thematic_category ||
              selected?.thematic_category ||
              selectedPin?.indicacao?.thematic_category ||
              "Outros";
            const catStyle = getCategoryStyle(catName);
            const isPolygon =
              geomPin.geometry_type === "Polygon" ||
              geomPin.geometry_type === "MultiPolygon";

            return (
              <GeoJSON
                key={`vector-${geomPin.id || idx}-${geomPin.geometry_type}-${selected?.id}`}
                data={geomPin.geometry_geojson}
                style={() => {
                  if (isPolygon) {
                    return {
                      color: catStyle.bgColor,
                      weight: 3,
                      fillColor: catStyle.bgColor,
                      fillOpacity: 0.28,
                      dashArray: geomPin.is_approximate ? "6, 6" : undefined,
                    };
                  }

                  // Trechos de rua (LineString ou MultiLineString) - Estilo Marca-Texto Cartográfico
                  return {
                    color: catStyle.bgColor,
                    weight: 4.5,
                    opacity: isDarkMode ? 0.65 : 0.55,
                    className: "subtle-street-line",
                    lineCap: "round",
                    lineJoin: "round",
                    dashArray: geomPin.is_approximate ? "6, 8" : undefined,
                  };
                }}
              />
            );
          })}

        {isChoroplethActive ? null : isHeatmapActive ? (
          <HeatmapLayer points={displayPins} />
        ) : (
          <MarkerClusterGroup
            ref={(node) => {
              clusterGroupRef.current = node;
              if (node && node !== clusterGroupInstance) {
                setClusterGroupInstance(node);
              }
            }}
            chunkedLoading
            maxClusterRadius={45}
            spiderfyOnMaxZoom={true}
            zoomToBoundsOnClick={false}
            spiderfyDistanceMultiplier={1.5}
            spiderLegPolylineOptions={{
              weight: 1.8,
              color: isDarkMode ? "#94a3b8" : "#64748b",
              opacity: 0.8,
              dashArray: "4, 4",
              className: "cluster-spider-leg",
            }}
            showCoverageOnHover={false}
            iconCreateFunction={createClusterIcon}
            onClick={(e) => {
              if (e?.originalEvent) {
                L.DomEvent.stopPropagation(e.originalEvent);
                L.DomEvent.preventDefault(e.originalEvent);
                e.originalEvent._stopped = true;
              }
              const cluster = e?.layer;
              if (!cluster || typeof cluster.getAllChildMarkers !== "function") return;
              const childMarkers = cluster.getAllChildMarkers();
              if (childMarkers.length < 2) return;

              const isSamePoint = areMarkersAtSameLocation(childMarkers, cluster);
              const clusterGroup = clusterGroupRef.current;

              if (isSamePoint) {
                // Mesma localização exata / colocalizada: expande em aranha (spiderfy) ou recolhe se já aberto
                const isOpen =
                  clusterGroup?._spiderfiedSet?.has(cluster) ||
                  clusterGroup?._spiderfied === cluster;

                if (isOpen) {
                  // Clicou no botão central ou no cluster já aberto: recolhe a aranha imediatamente
                  delete clusterGroup._spiderfyLockUntil;
                  cluster.unspiderfy();
                } else {
                  if (clusterGroup) {
                    clusterGroup._spiderfyLockUntil = Date.now() + 1000;
                    clusterGroup._lastSpiderfyTime = Date.now();
                  }
                  cluster.spiderfy();
                }
              } else {
                // Localizações diferentes: aproxima o zoom para separar os marcadores naturalmente no mapa
                cluster.zoomToBounds({ padding: [45, 45] });
              }
            }}
          >
            {(displayPins || []).map((pin) => (
              <PinMarker
                key={pin.id}
                pin={pin}
                isSelected={selected?.id === pin.indicacao_id}
                onSelect={select}
                clusterGroupRef={clusterGroupRef}
                markerMapRef={markerMapRef}
              />
            ))}
          </MarkerClusterGroup>
        )}
      </MapContainer>
    </div>
  );
}
